/**
 * Auth API — TanStack Query hooks + plain async functions.
 *
 * Every call uses the configured `api` Axios instance (base '/api/v1',
 * withCredentials:true, 401-silent-refresh, envelope-normalisation).
 *
 * Server envelope: { data: T, error: { code, message } | null, meta: ... }
 * The error interceptor in client.ts already normalises rejections to
 * `Error & { code: string }` — callers read `err.code` directly.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { useAuthStore } from '@/store/authStore';

// ---------------------------------------------------------------------------
// Envelope shape (server contract)
// ---------------------------------------------------------------------------
interface ApiEnvelope<T> {
  data: T;
  error: { code: string; message: string } | null;
  meta?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Response shapes
// ---------------------------------------------------------------------------
export interface LoginResponse {
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
  mustChangePassword: boolean;
  // The backend nests the signed-in identity under `user`.
  user: {
    id: string;
    email: string;
    role: string;
  };
}

export interface MeResponse {
  id: string;
  email: string;
  displayName: string;
  role: string;
  mustChangePassword: boolean;
}

// ---------------------------------------------------------------------------
// Plain async functions (usable outside React, e.g. in guards)
// ---------------------------------------------------------------------------

/** POST /auth/login */
export async function loginFn(email: string, password: string): Promise<LoginResponse> {
  const res = await api.post<ApiEnvelope<LoginResponse>>('/auth/login', { email, password });
  return res.data.data;
}

/** POST /auth/change-password */
export async function changePasswordFn(
  currentPassword: string,
  newPassword: string,
): Promise<LoginResponse> {
  const res = await api.post<ApiEnvelope<LoginResponse>>('/auth/change-password', {
    currentPassword,
    newPassword,
  });
  return res.data.data;
}

/** POST /auth/logout */
export async function logoutFn(): Promise<void> {
  await api.post('/auth/logout');
}

/** GET /auth/me */
export async function getMeFn(): Promise<MeResponse> {
  const res = await api.get<ApiEnvelope<MeResponse>>('/auth/me');
  return res.data.data;
}

/**
 * Restore the session on app boot. The access token is in-memory only, so a
 * hard reload drops it — but the httpOnly refresh cookie (sent via
 * withCredentials) is still valid. Calling GET /auth/me with no bearer makes
 * client.ts's 401 interceptor silently refresh (single-flight) and replay,
 * which mints a fresh access token into the store; we then layer on the
 * identity (role/email/mustChangePassword) and report success. On any failure
 * (no/expired cookie) the store is cleared and the caller routes to /login.
 */
export async function bootstrapSession(): Promise<boolean> {
  try {
    const me = await getMeFn();
    const store = useAuthStore.getState();
    if (!store.accessToken) return false;
    store.setLogin(store.accessToken, me.role, me.email, me.mustChangePassword, me.id);
    return true;
  } catch {
    useAuthStore.getState().clearAuth();
    return false;
  }
}

// ---------------------------------------------------------------------------
// TanStack Query hooks
// ---------------------------------------------------------------------------

// Every auth mutation below sets `meta: { suppressGlobalErrorToast: true }`.
// The caller already owns error UX for these flows — LoginPage and
// ChangePasswordPage catch the rejection and render their own inline
// serverError alert, and useLogout's local sign-out (clearAuth + cache clear,
// in onSettled below) proceeds regardless of whether the network call
// succeeded — so App.tsx's global MutationCache backstop would otherwise
// stack a redundant (or, for logout, entirely spurious) "wasn't saved" toast
// on top of that. See App.tsx's mutationCache.onError for the other half of
// this contract.

/** Mutation: login + populate auth store */
export function useLogin() {
  const { setLogin } = useAuthStore();

  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      loginFn(email, password),
    onSuccess: (data) => {
      setLogin(data.accessToken, data.user.role, data.user.email, data.mustChangePassword, data.user.id);
    },
    meta: { suppressGlobalErrorToast: true },
  });
}

/** Mutation: change password — session rotation is handled by the page's onSubmit */
export function useChangePassword() {
  return useMutation({
    mutationFn: ({
      currentPassword,
      newPassword,
    }: {
      currentPassword: string;
      newPassword: string;
    }) => changePasswordFn(currentPassword, newPassword),
    meta: { suppressGlobalErrorToast: true },
  });
}

/** Mutation: logout + clear auth store */
export function useLogout() {
  const { clearAuth } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: logoutFn,
    onSettled: () => {
      clearAuth();
      queryClient.clear();
    },
    meta: { suppressGlobalErrorToast: true },
  });
}

/** Query: /auth/me — only fetches when an access token is present */
export function useMe() {
  const { accessToken } = useAuthStore();

  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: getMeFn,
    enabled: !!accessToken,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}
